/**
 * Read-only summary of saved "Incorrect Evidence" feedback for a criterion.
 *
 * Renders the facts the reviewer flagged as incorrect, grouped into supportive
 * and contradictory sub-sections, each followed by the reviewer's per-evidence
 * correction. An "Edit" affordance reopens the feedback form.
 * @module IncorrectEvidenceSummary
 */
import React from "react";
import { EvidenceFact } from "../../../data-model/nccnGuidelines";
import { ChunkLocation } from "../utils/evidenceText";
import { EvidenceCorrections } from "../utils/nccnReviewFeedback";
import { VERDICT_COLORS } from "../utils/verdictColors";
import { FeedbackSummaryCard } from "./FeedbackSummaryCard";
import { FlaggedEntry, FlaggedEvidenceGroup } from "./FlaggedEvidenceGroup";

/** Props for {@link IncorrectEvidenceSummary}. */
interface IncorrectEvidenceSummaryProps {
  supportiveFacts: EvidenceFact[];
  contradictoryFacts: EvidenceFact[];
  corrections: EvidenceCorrections;
  onEdit: () => void;
  /**
   * Chunk-location lookup for the parent item, so flagged facts' `DocChunk#N`
   * references stay clickable. Absent when the item has no locations.
   */
  locations?: Map<number, ChunkLocation>;
}

/**
 * Selects the facts a reviewer flagged as incorrect and pairs each with its
 * correction from the corrections map.
 *
 * @param facts - Evidence facts for one tone (supportive or contradictory)
 * @param corrections - Per-evidence correction map keyed by fact text
 * @returns The flagged facts with their corrections
 */
const flaggedEntries = (
  facts: EvidenceFact[],
  corrections: EvidenceCorrections,
): FlaggedEntry[] =>
  facts
    .filter((fact) => fact.user_validation === false)
    .map((fact) => ({
      text: fact.text,
      correction: corrections[fact.text] ?? "",
    }));

/**
 * Renders the saved incorrect-evidence summary card.
 *
 * @param props - Flagged facts by tone, the corrections map, and the edit handler
 * @returns The rendered summary, or null when no evidence is flagged
 */
export const IncorrectEvidenceSummary: React.FC<
  IncorrectEvidenceSummaryProps
> = ({
  supportiveFacts,
  contradictoryFacts,
  corrections,
  onEdit,
  locations,
}) => {
  const supportive = flaggedEntries(supportiveFacts, corrections);
  const contradictory = flaggedEntries(contradictoryFacts, corrections);
  if (!supportive.length && !contradictory.length) return null;

  return (
    <FeedbackSummaryCard title="Incorrect Evidence" onEdit={onEdit}>
      <FlaggedEvidenceGroup
        title="Incorrect Supportive Evidence"
        background={VERDICT_COLORS.met.background}
        color={VERDICT_COLORS.met.text}
        entries={supportive}
        locations={locations}
      />
      {supportive.length > 0 && contradictory.length > 0 && (
        <div className="h-[0.0625rem] self-stretch border-t border-dashed border-[rgba(102,93,0,0.20)]" />
      )}
      <FlaggedEvidenceGroup
        title="Incorrect Contradictory Evidence"
        background={VERDICT_COLORS.notMet.background}
        color={VERDICT_COLORS.notMet.text}
        entries={contradictory}
        locations={locations}
      />
    </FeedbackSummaryCard>
  );
};
